import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, RadialGauge, Pill, AiCard, StatCard } from "@/components/perf";
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { DollarSign, TrendingUp, Package, Users, AlertTriangle, Info, Sparkles, CalendarPlus, Trash2, CalendarCheck, ChevronRight, X, Mail, Megaphone, Pencil, CalendarX2, Activity, RotateCcw, Target, Trophy, GraduationCap, Award, CreditCard, Lock, ShieldCheck, Clapperboard, KeyRound, Swords, Music, FlaskConical } from "lucide-react";
import CommunityModeration from "@/components/CommunityModeration";
import { toast } from "sonner";
import BplMatchCenter from "@/components/BplMatchCenter";
import PbrAdminConsole from "@/components/PbrAdminConsole";
import ConciergeLimitsPanel from "@/components/ConciergeLimitsPanel";
import { BrainQuotaPanel } from "@/components/BrainQuotaPanel";
import SlotManager from "@/components/SlotManager";
import MembershipCardsPanel from "@/components/MembershipCardsPanel";
import LeagueRoster from "@/components/LeagueRoster";
import LeagueScheduler from "@/components/LeagueScheduler";
import LeagueRegistrationControl from "@/components/LeagueRegistrationControl";
import InventoryManager from "@/components/InventoryManager";
import FinancialControls from "@/components/FinancialControls";
import CoachManager from "@/components/CoachManager";
import AdminSection, { OrderedSections } from "@/components/AdminSection";
import { useContent } from "@/context/ContentContext";
import ServiceFeesPanel from "@/components/ServiceFeesPanel";
import PlatformFeePanel from "@/components/PlatformFeePanel";
import LiveBroadcastPanel from "@/components/LiveBroadcastPanel";
import PlayerDirectory from "@/components/PlayerDirectory";
import TournamentHub from "@/components/TournamentHub";
import PromoLauncher from "@/components/PromoLauncher";
import ActivityFeed from "@/components/ActivityFeed";
import { PsiControl } from "@/components/PsiControl";
import PaymentsPanel from "@/components/PaymentsPanel";
import VideoPricingPanel from "@/components/VideoPricingPanel";
import CardPricingPanel from "@/components/CardPricingPanel";
import VaultCommandCenter from "@/components/VaultCommandCenter";
import CardWarsControl from "@/components/CardWarsControl";
import CardForge from "@/components/CardForge";
import RoyaltyRosterPanel from "@/components/RoyaltyRosterPanel";
import RedemptionDesk from "@/components/RedemptionDesk";
import PoolApprovalPanel from "@/components/PoolApprovalPanel";
import TreasuryController from "@/components/TreasuryController";
import MerchantManager from "@/components/MerchantManager";
import AdManagerPanel from "@/components/AdManagerPanel";
import EventRecordsPanel from "@/components/EventRecordsPanel";
import BroadcastPanel from "@/components/BroadcastPanel";
import SiteContentPanel from "@/components/SiteContentPanel";
import SystemManual from "@/components/SystemManual";
import AthleteInvitePanel from "@/components/AthleteInvitePanel";
import AdminBrain from "@/components/AdminBrain";
import OrdersPanel from "@/components/OrdersPanel";
import GoogleFeedPanel from "@/components/GoogleFeedPanel";
import ShareRewardsPanel from "@/components/ShareRewardsPanel";
import DuprPanel from "@/components/DuprPanel";
import AutographPanel from "@/components/AutographPanel";
import RegistryPanel from "@/components/RegistryPanel";
import PageAccessPanel from "@/components/PageAccessPanel";
import GalleryControl from "@/components/GalleryControl";
import ThankYouControl from "@/components/ThankYouControl";
import AffiliateControl from "@/components/AffiliateControl";
import BullionControl from "@/components/BullionControl";
import SoundtrackManager from "@/components/SoundtrackManager";
import DemoTournamentLab from "@/components/DemoTournamentLab";
import ArenaBackdropPanel from "@/components/ArenaBackdropPanel";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function VaultVisibilityToggle() {
  const { content, save } = useContent();
  const off = content?.["access.vault"] === "off";
  const flip = async () => {
    if (off) {
      await save("access.vault", content?.["access.vault.prev"] || "everyone");
      toast.success("Card Vault is back on — visible in the nav again");
    } else {
      await save("access.vault.prev", content?.["access.vault"] || "everyone", { quiet: true });
      await save("access.vault", "off");
      toast.success("Card Vault turned off — hidden from everyone");
    }
  };
  return (
    <div className="flex items-center justify-between gap-4 border border-pb-border bg-pb-elevated px-4 py-3 mb-5" data-testid="vault-toggle-row">
      <div>
        <div className={`text-xs font-black uppercase tracking-widest ${off ? "text-zinc-400" : "text-[#A3E635]"}`}>
          Card Vault is {off ? "OFF" : "LIVE"}
        </div>
        <div className="text-[10px] text-zinc-500 mt-0.5">
          {off ? "Hidden from the nav and blocked for all members. Flip the switch to bring it back." : "Visible to members. Flip the switch to hide the whole Card Vault instantly."}
        </div>
      </div>
      <button data-testid="vault-toggle-switch" role="switch" aria-checked={!off} onClick={flip}
        className={`relative w-12 h-6 rounded-full shrink-0 transition-colors ${off ? "bg-zinc-700" : "bg-[#A3E635]"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all duration-200 ${off ? "left-0.5" : "left-[26px]"}`} />
      </button>
    </div>
  );
}

export default function Admin() {  const { user } = useAuth();
  const [biz, setBiz] = useState(null);
  const [health, setHealth] = useState(null);
  const [insight, setInsight] = useState(null);
  const [insightAt, setInsightAt] = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [membersModal, setMembersModal] = useState(null); // { tier, label }
  const [membersList, setMembersList] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [pwReset, setPwReset] = useState(null);
  const [att, setAtt] = useState({});

  const resetMemberPassword = async (m) => {
    if (!window.confirm(`Reset ${m.name}'s password? They'll get a temporary password and must set their own at next sign-in.`)) return;
    try {
      const { data } = await api.post(`/admin/members/${m.id}/reset-password`);
      setPwReset(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const openMembers = async (tier, label) => {
    setMembersModal({ tier, label });
    setMembersLoading(true);
    try {
      const { data } = await api.get("/admin/members", { params: { membership: tier } });
      setMembersList(data);
    } catch {
      setMembersList([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const refreshMembers = async (tier) => {
    const t = tier || membersModal?.tier;
    if (!t) return;
    const { data } = await api.get("/admin/members", { params: { membership: t } });
    setMembersList(data);
    loadOverview();
  };

  const changeMembership = async (m, membership) => {
    if (membership === m.membership) return;
    try {
      await api.put(`/admin/members/${m.id}/membership`, { membership });
      toast.success(`${m.name} moved to ${membership.toUpperCase()}`);
      await refreshMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const changeSkill = async (m, skill_level) => {
    try {
      await api.put(`/admin/members/${m.id}/skill`, { skill_level: skill_level || null });
      toast.success(`${m.name} skill set to ${skill_level || "Auto (AI)"}`);
      await refreshMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Permanently delete ${m.name} and all of their data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success(`${m.name} deleted`);
      await refreshMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const loadOverview = async () => {
    const [o, a] = await Promise.all([api.get("/admin/overview"), api.get("/admin/alerts")]);
    setBiz(o.data); setAlerts(a.data);
    api.get("/admin/attention").then(({ data }) => setAtt(data)).catch(() => {});
  };

  const loadHealth = async () => {
    try { const { data } = await api.get("/admin/health"); setHealth(data.health); setInsight(data.insight); setInsightAt(data.insight_generated_at); }
    finally { setInsightLoading(false); }
  };

  const refreshInsights = async () => {
    setInsightLoading(true);
    try { const { data } = await api.get("/admin/health", { params: { refresh: true } }); setHealth(data.health); setInsight(data.insight); setInsightAt(data.insight_generated_at); }
    finally { setInsightLoading(false); }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadOverview();
    loadHealth();
  }, [user]); // eslint-disable-line

  useAutoRefresh(() => {
    if (user?.role !== "admin") return;
    loadOverview();
  });

  const revData = biz ? [
    { name: "Store", value: biz.revenue.store, color: "#A3E635" },
    { name: "Sessions", value: biz.revenue.sessions, color: "#4ADE80" },
    { name: "Leagues", value: biz.revenue.leagues, color: "#16A34A" },
    { name: "Memberships", value: biz.revenue.memberships || 0, color: "#86EFAC" },
  ] : [];

  return (
    <div>
      {user && user.role === "scorekeeper" && <Navigate to="/scorekeeper" replace />}
      {user && user.role !== "admin" && user.role !== "scorekeeper" && !user.is_td && !user.is_coach && <Navigate to="/training" replace />}
      {user && user.role !== "admin" && user.is_coach && (
        <div data-testid="coach-admin-view">
          <PageHeader kicker="Coach Console" title="Session Availability"
            subtitle="Create and manage drill-session slots — players book these from the Book tab." />
          <SlotManager />
        </div>
      )}
      {user && user.role !== "admin" && user.is_td && (
        <div data-testid="td-admin-view">
          <PageHeader kicker="Director Console" title="TD Tournament Hub"
            subtitle="Create and run your tournaments — brackets, live scores, and finals." />
          <TournamentHub />
          {user.demo_lab_approved && (
            <div className="mt-8 bg-pb-surface border border-cyan-400/30 p-5" data-testid="td-demo-lab-section">
              <div className="flex items-center gap-2 mb-3">
                <FlaskConical className="w-5 h-5 text-pb-cyan" />
                <span className="font-display font-bold text-lg uppercase">Tournament Demo Lab</span>
                <Pill color="#22D3EE">Admin-approved access</Pill>
              </div>
              <DemoTournamentLab />
            </div>
          )}
        </div>
      )}
      {user && user.role === "admin" && (<>
      <PageHeader kicker="Business Intelligence · Operating System"
        title="Admin Console"
        subtitle="Real-time financials across training, league and store — with an AI-driven business health score and growth intelligence." />

      {/* Top financial stats */}
      <OrderedSections>
      <AdminSection title="Bullpen Brain — Owner's AI" icon={Activity} testid="section-admin-brain">
        <AdminBrain />
      </AdminSection>

      <AdminSection title="Player Directory — Name · Phone · Email" icon={Users} testid="section-player-directory">
        <PlayerDirectory />
      </AdminSection>

      <AdminSection title="DUPR Integration (UAT sandbox)" icon={Activity} testid="section-dupr">
        <DuprPanel />
      </AdminSection>

      <AdminSection title="Live Activity" icon={Activity} testid="section-activity">
        <ActivityFeed />
      </AdminSection>

      <AdminSection title="PSI Control — Player Ratings" icon={Activity} testid="section-psi-control">
        <PsiControl />
      </AdminSection>

      <AdminSection title="Message All Members" icon={Megaphone} testid="section-broadcast">
        <BroadcastPanel />
      </AdminSection>

      <AdminSection title="Site Soundtrack — Music Player" icon={Music} testid="section-soundtrack">
        <SoundtrackManager />
      </AdminSection>

      <AdminSection title="Tournament Demo Lab — Rehearse the Full Flow" icon={FlaskConical} testid="section-demo-lab">
        <DemoTournamentLab />
      </AdminSection>

      <AdminSection title="PBR Rankings — Points, Qualification & Wild Cards" icon={Trophy} testid="section-pbr">
        <PbrAdminConsole />
      </AdminSection>

      <AdminSection title="AI Concierge — Daily Question Limits" icon={Activity} testid="section-concierge-limits">
        <ConciergeLimitsPanel />
        <BrainQuotaPanel />
      </AdminSection>

      <AdminSection title="Financial Overview" icon={DollarSign} testid="section-financials">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="biz-revenue" label="Total Revenue" value={fmt(biz?.revenue.total)} accent="#A3E635" />
        <StatCard testid="biz-profit" label="Net Profit" value={fmt(biz?.net_profit)} accent="#4ADE80" />
        <StatCard testid="biz-margin" label="Net Margin" value={biz?.margin ?? 0} unit="%" accent="#84CC16" />
        <StatCard testid="biz-orders" label="Store Orders" value={biz?.orders.count ?? 0} accent="#A3E635" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Health score */}
        <div className="bg-pb-surface border border-pb-border p-6" data-testid="health-score">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">AI Business Health Score</div>
          <div className="flex flex-col items-center">
            <RadialGauge value={health?.score ?? 0} label={health?.band || "—"} color="#A3E635" size={180} />
          </div>
          <div className="mt-4 space-y-2">
            {health?.components.map((c, i) => (
              <div key={i}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-zinc-300">{c.label}</span>
                  <span className="font-mono text-pb-red">{c.value}</span>
                </div>
                <div className="h-1.5 bg-pb-elevated"><div className="h-full bg-pb-red" style={{ width: `${c.value}%` }} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue breakdown */}
        <div className="bg-pb-surface border border-pb-border p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">Revenue Streams</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revData}>
              <XAxis dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 12 }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <YAxis tick={{ fill: "#52525B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#1A1A1A", border: "1px solid #27272A", color: "#fff" }} />
              <Bar dataKey="value" radius={[0, 0, 0, 0]}>
                {revData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-center">
            <Mini label="Expenses" value={fmt(biz?.expenses.total)} color="#EF4444" />
            <Mini label="Proc. Fees" value={fmt(biz?.fees)} color="#F59E0B" />
            <Mini label="Taxes (8%)" value={fmt(biz?.taxes)} color="#F59E0B" />
            <Mini label="Members" value={biz?.members.total ?? 0} color="#A3E635" />
          </div>
        </div>
      </div>

      <PaymentsPanel />
      </AdminSection>

      <AdminSection title="AI Insights & Promotions" icon={Sparkles} testid="section-ai">
      <div className="mb-8 space-y-4">
        <AiCard loading={insightLoading}>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-pb-cyan mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-zinc-200 leading-relaxed" data-testid="admin-insight-text">
                {insight || "No analysis in the last 24 hours — hit Refresh Insights for a fresh AI read on the business."}
              </p>
              {insightAt && <div className="text-[9px] uppercase tracking-widest text-zinc-600 mt-1.5">Generated {new Date(insightAt).toLocaleString()} · cached 24h</div>}
            </div>
            <button data-testid="refresh-insights-btn" disabled={insightLoading} onClick={refreshInsights}
              className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50 shrink-0">
              <Sparkles className="w-3.5 h-3.5" /> Refresh Insights
            </button>
          </div>
        </AiCard>
        <div className="bg-pb-surface border border-pb-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">AI Specials &amp; Promotions</span>
          </div>
          <PromoLauncher onChange={() => { loadOverview(); }} />
        </div>
      </div>
      </AdminSection>

      <AdminSection title="Financial Controls & Pricing" icon={Pencil} testid="section-controls">
        <FinancialControls onSaved={() => { loadOverview(); loadHealth(); }} />
      </AdminSection>

      <AdminSection title="AI Video Pricing" icon={Clapperboard} testid="section-video-pricing">
        <VideoPricingPanel />
      </AdminSection>

      <AdminSection title="AI Sports Card Pricing" icon={Sparkles} testid="section-card-pricing">
        <CardPricingPanel />
      </AdminSection>

      <AdminSection title="Card Vault Command Center" icon={Target} testid="section-card-vault" badge={att["section-card-vault"]}>
        <VaultVisibilityToggle />
        <VaultCommandCenter />
      </AdminSection>

      <AdminSection title="Card Wars Command Center" icon={Swords} testid="section-cardwars">
        <p className="text-xs text-zinc-500 mb-4">Run the whole game from here — Court Clash economy, the Friday Night pot, protection pricing and every pack's dollar/coin price, odds and guarantees.</p>
        <CardWarsControl />
      </AdminSection>

      <AdminSection title="Arena Backdrop — Featured Cards" icon={Swords} testid="section-arena-backdrop">
        <ArenaBackdropPanel />
      </AdminSection>

      <AdminSection title="Pool Pack Approvals" icon={ShieldCheck} testid="section-pool-approvals">
        <PoolApprovalPanel />
      </AdminSection>

      <AdminSection title="Card Forge — Exclusives, Base Cards & Parallels" icon={Award} testid="section-card-forge">
        <RoyaltyRosterPanel />
        <div className="border-t border-pb-border my-5" />
        <CardForge />
      </AdminSection>

      <AdminSection title="Verified Fan Signed Cards — Live Autographs" icon={Pencil} testid="section-autographs">
        <AutographPanel />
      </AdminSection>

      <AdminSection title="Founding Athlete Invites — Recruitment" icon={Award} testid="section-athlete-invites" badge={att["section-athlete-invites"]}>
        <AthleteInvitePanel />
      </AdminSection>

      <AdminSection title="The Registry — Master Card Ledger" icon={ShieldCheck} testid="section-registry">
        <RegistryPanel />
      </AdminSection>

      <AdminSection title="How The System Works — Owner's Manual" icon={Info} testid="section-system-manual">
        <SystemManual />
      </AdminSection>

      <AdminSection title="Membership Cards" icon={CreditCard} testid="section-membership-cards">
        <MembershipCardsPanel />
      </AdminSection>

      <AdminSection title="Affiliate Program" icon={Megaphone} testid="section-affiliates">
        <AffiliateControl />
      </AdminSection>

      <AdminSection title="Bullion Coin" icon={CreditCard} testid="section-bullion" badge={att["section-bullion"]}>
        <BullionControl />
      </AdminSection>

      <AdminSection title="Event Records" icon={Trophy} testid="section-event-records">
        <EventRecordsPanel />
      </AdminSection>

      <AdminSection title="Stripe Service Fees" icon={CreditCard} testid="section-service-fees">
        <ServiceFeesPanel />
      </AdminSection>

      <AdminSection title="Live Broadcast — Twitch, Main Court & Scorekeepers" icon={CreditCard} testid="section-live">
        <LiveBroadcastPanel />
      </AdminSection>

      <AdminSection title="Platform Fee — Tournaments & Leagues" icon={CreditCard} testid="section-platform-fee">
        <PlatformFeePanel />
      </AdminSection>

      <AdminSection title="Ad Control Panel" icon={Megaphone} testid="section-ads">
        <AdManagerPanel />
      </AdminSection>

      <AdminSection title="AI Treasury Controller" icon={TrendingUp} testid="section-treasury">
        <TreasuryController />
      </AdminSection>

      <AdminSection title="Merchant Network" icon={Package} testid="section-merchants" badge={att["section-merchants"]}>
        <MerchantManager />
      </AdminSection>

      <AdminSection title="BLC Redemption Desk" icon={DollarSign} testid="section-redemption" badge={att["section-redemption"]}>
        <RedemptionDesk />
      </AdminSection>

      <AdminSection title="Site Content Edits" icon={Pencil} testid="section-site-content">
        <SiteContentPanel />
      </AdminSection>

      <AdminSection title="Page Access Control" icon={Lock} testid="section-page-access">
        <PageAccessPanel />
      </AdminSection>

      <AdminSection title="Community Moderation" icon={ShieldCheck} testid="section-moderation">
        <CommunityModeration />
      </AdminSection>

      <AdminSection title="Members & Alerts" icon={Users} testid="section-members">
      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        {/* Membership mix */}
        <div className="bg-pb-surface border border-pb-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">Membership Mix</span>
          </div>
          {biz && [
            { l: "Free / Rookie", v: biz.members.free, c: "#A1A1AA", tier: "free" },
            { l: "Paid (VIP/ACE)", v: biz.members.vip, c: "#A3E635", tier: "vip" },
          ].map((m) => (
            <button key={m.tier} data-testid={`member-row-${m.tier}`} onClick={() => openMembers(m.tier, m.l)}
              className="w-full flex items-center justify-between py-2 border-b border-pb-border/40 text-sm hover:bg-pb-elevated/60 px-1 -mx-1 transition-colors group">
              <span className="text-zinc-300 group-hover:text-white flex items-center gap-1.5">{m.l}<ChevronRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-pb-red" /></span>
              <span className="font-display font-black text-lg" style={{ color: m.c }}>{m.v}</span>
            </button>
          ))}
        </div>

        {/* Operational alerts */}
        <div className="bg-pb-surface border border-pb-border p-5" data-testid="alerts">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">Operational Alerts</span>
          </div>
          <div className="space-y-3">
            {alerts.map((a, i) => {
              const Icon = a.level === "danger" || a.level === "warning" ? AlertTriangle : Info;
              const color = a.level === "danger" ? "#EF4444" : a.level === "warning" ? "#F59E0B" : "#4ADE80";
              return (
                <div key={i} className="flex gap-3 items-start">
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
                  <div>
                    <div className="text-sm font-semibold">{a.title}</div>
                    <div className="text-xs text-zinc-500">{a.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </AdminSection>

      <AdminSection title="Store Inventory, Catalog & Order Fulfillment" icon={Package} testid="section-inventory" badge={att["section-orders"]}>
        <InventoryManager />
        <GoogleFeedPanel />
        <ShareRewardsPanel />
        <div className="mt-8" data-testid="section-orders"><OrdersPanel /></div>
      </AdminSection>

      <AdminSection title="Session Availability" icon={CalendarCheck} testid="section-slots">
        <SlotManager />
      </AdminSection>

      <AdminSection title="Coaches System" icon={GraduationCap} testid="section-coaches" badge={att["section-coaches"]}>
        <CoachManager />
      </AdminSection>

      <AdminSection title="Tournament Hub" icon={Trophy} testid="section-tournaments" badge={att["section-tournaments"]}>
        <a href="/admin/tournament-intel" data-testid="open-intel-btn"
          className="flex items-center justify-center gap-2 border border-pb-cyan/60 bg-pb-cyan/5 text-pb-cyan font-black uppercase tracking-widest text-[11px] px-4 py-3 mb-4 hover:bg-pb-cyan hover:text-black transition-all">
          📊 Tournament Intelligence — every event, every number, AI coaching
        </a>
        <TournamentHub />
      </AdminSection>

      <AdminSection title="Tournament Gallery — Public Photo Walls" icon={Clapperboard} testid="section-gallery">
        <GalleryControl />
      </AdminSection>

      <AdminSection title="Thank You Letters — Gold Post-Tournament Send" icon={Mail} testid="section-thankyou">
        <ThankYouControl />
      </AdminSection>

      <AdminSection title="Drill Rep Presets" icon={Target} testid="section-reps">
        <RepPresets />
      </AdminSection>

      <AdminSection title="1 Billion Rep Challenge" icon={RotateCcw} testid="section-challenge">
        <ChallengeReset />
      </AdminSection>

      <AdminSection title="BPL League Management" icon={Award} testid="section-bpl">
        <LeagueRegistrationControl />
        <LeagueRoster />
        <LeagueScheduler />
        <BplMatchCenter />
      </AdminSection>
      </OrderedSections>


      {membersModal && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setMembersModal(null)}>
          <div className="bg-pb-surface border border-pb-red w-full max-w-2xl max-h-[80vh] flex flex-col animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="members-modal">
            <div className="flex items-center justify-between p-5 border-b border-pb-border">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-pb-red" />
                <h3 className="font-display font-black text-xl uppercase">{membersModal.label} Members</h3>
                <span className="text-sm text-zinc-500 font-mono">({membersList.length})</span>
              </div>
              <button data-testid="members-modal-close" onClick={() => setMembersModal(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto">
              {pwReset && (
                <div className="mx-5 mt-4 border border-[#A3E635]/50 bg-[#A3E635]/10 p-3 text-sm break-words overflow-hidden" data-testid="pw-reset-banner">
                  Temporary password for <b>{pwReset.name}</b> ({pwReset.email}):{" "}
                  <span className="font-mono font-black text-[#A3E635]" data-testid="pw-reset-temp">{pwReset.temp_password}</span>
                  <button data-testid="pw-reset-copy" onClick={() => { navigator.clipboard.writeText(pwReset.temp_password); toast.success("Copied"); }}
                    className="ml-3 text-xs underline text-zinc-300 hover:text-white">Copy</button>
                  <button data-testid="pw-reset-dismiss" onClick={() => setPwReset(null)} className="ml-3 text-xs underline text-zinc-500 hover:text-white">Dismiss</button>
                  <div className="text-[10px] text-zinc-500 mt-1">Shown once — share it with the member. They'll be required to choose their own password at next sign-in.</div>
                </div>
              )}
              {membersLoading ? (
                <div className="p-6 text-sm text-zinc-500 animate-pulse">Loading members…</div>
              ) : membersList.length === 0 ? (
                <div className="p-6 text-sm text-zinc-600">No members in this tier yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-pb-surface">
                    <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-pb-border/60">
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3 hidden md:table-cell">Phone</th>
                      <th className="text-right px-5 py-3 hidden lg:table-cell">Sessions</th>
                      <th className="text-right px-5 py-3 hidden lg:table-cell">Spend</th>
                      <th className="text-left px-5 py-3">Manage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersList.map((m, i) => (
                      <tr key={m.id || m.email || i} data-testid="member-row" className="border-b border-pb-border/40 hover:bg-pb-elevated/50">
                        <td className="px-5 py-3 font-semibold whitespace-nowrap">{m.name}</td>
                        <td className="px-5 py-3">
                          <a href={`mailto:${m.email}`} className="text-pb-cyan hover:underline inline-flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5" />{m.email}
                          </a>
                        </td>
                        <td className="px-5 py-3 text-zinc-400 hidden md:table-cell">{m.phone || "—"}</td>
                        <td className="px-5 py-3 text-right font-mono text-zinc-300 hidden lg:table-cell">{m.total_sessions_booked ?? 0}</td>
                        <td className="px-5 py-3 text-right font-mono text-pb-red hidden lg:table-cell">${Number(m.total_spend || 0).toFixed(2)}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <select data-testid={`member-membership-${m.id}`} value={m.membership} onChange={(e) => changeMembership(m, e.target.value)}
                              className="bg-pb-elevated border border-pb-border text-xs px-2 py-1.5 focus:outline-none focus:border-pb-red">
                              <option value="free">Free</option>
                              <option value="vip">VIP</option>
                              <option value="ace">ACE</option>
                            </select>
                            <select data-testid={`member-skill-${m.id}`} value={m.skill_override || ""} onChange={(e) => changeSkill(m, e.target.value)}
                              className="bg-pb-elevated border border-pb-border text-xs px-2 py-1.5 focus:outline-none focus:border-pb-red">
                              <option value="">Auto</option>
                              <option value="Low Intermediate">Low Intermediate</option>
                              <option value="Intermediate">Intermediate</option>
                              <option value="High Intermediate">High Intermediate</option>
                              <option value="Advanced">Advanced</option>
                              <option value="Pro">Pro</option>
                            </select>
                            <button data-testid={`member-reset-pw-${m.id}`} onClick={() => resetMemberPassword(m)} title="Reset password"
                              className="w-7 h-7 flex items-center justify-center border border-pb-border hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                              <KeyRound className="w-3.5 h-3.5" />
                            </button>
                            <button data-testid={`member-delete-${m.id}`} onClick={() => deleteMember(m)} title="Delete member"
                              className="w-7 h-7 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

function RepPresets() {
  const [presets, setPresets] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const LEVEL_COLORS = { "Low Intermediate": "#22C55E", Intermediate: "#EAB308", "High Intermediate": "#F97316", Advanced: "#EF4444", Pro: "#A855F7" };

  const load = async () => {
    const { data } = await api.get("/admin/rep-presets");
    setPresets(data.presets);
    setDefaults(data.defaults);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/rep-presets", {
        "Low Intermediate": Number(presets["Low Intermediate"]), Intermediate: Number(presets.Intermediate),
        "High Intermediate": Number(presets["High Intermediate"]),
        Advanced: Number(presets.Advanced), Pro: Number(presets.Pro),
      });
      toast.success("Rep presets updated — live in the drill log");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save presets"); }
    finally { setSaving(false); }
  };

  if (!presets) return null;
  return (
    <div className="mt-8 bg-pb-surface border border-pb-border p-5" data-testid="rep-presets-card">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-5 h-5 text-pb-red" />
        <span className="font-display font-bold text-lg uppercase">Drill Rep Presets</span>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Default "Reps Attempted" pre-filled in the drill log, per level. Players can still change the number before logging.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {["Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Pro"].map((lvl) => (
          <div key={lvl}>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 font-bold" style={{ color: LEVEL_COLORS[lvl] }}>{lvl}</label>
            <input data-testid={`rep-preset-${lvl.toLowerCase().replace(/\s+/g, "-")}`} type="number" min={1} max={10000} value={presets[lvl]}
              onChange={(e) => setPresets({ ...presets, [lvl]: e.target.value })}
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 font-mono text-lg text-white focus:outline-none focus:border-pb-red" />
            <div className="text-[10px] text-zinc-600 mt-1 font-mono">Default: {defaults[lvl]} reps</div>
          </div>
        ))}
      </div>
      <button data-testid="save-rep-presets-btn" onClick={save} disabled={saving}
        className="mt-4 flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
        {saving ? "Saving…" : "Save Rep Presets"}
      </button>
    </div>
  );
}

function ChallengeReset() {
  const [total, setTotal] = useState(null);
  const load = async () => {
    const { data } = await api.get("/admin/challenge");
    setTotal(data.total_reps);
  };
  useEffect(() => { load(); }, []);
  const reset = async () => {
    if (!window.confirm("Reset the 1 Billion Rep Challenge counter to 0? This resets the global community counter for everyone.")) return;
    await api.post("/admin/challenge/reset");
    toast.success("Rep counter reset to 0");
    load();
  };
  return (
    <div className="mt-8 bg-pb-surface border border-pb-border p-5 flex flex-wrap items-center gap-4" data-testid="challenge-reset-card">
      <Activity className="w-6 h-6 text-pb-red shrink-0" />
      <div className="flex-1 min-w-[200px]">
        <div className="font-display font-bold text-lg uppercase">1 Billion Rep Challenge</div>
        <div className="text-xs text-zinc-500">Global community counter: <span className="text-pb-cyan font-mono" data-testid="challenge-total">{total != null ? total.toLocaleString() : "…"}</span> reps toward 1,000,000,000</div>
      </div>
      <button data-testid="reset-challenge-btn" onClick={reset}
        className="flex items-center gap-2 border border-pb-border hover:border-red-500 hover:text-red-400 font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-colors">
        <RotateCcw className="w-4 h-4" /> Reset Counter
      </button>
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div className="bg-pb-elevated border border-pb-border p-3">
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="font-display font-black text-lg" style={{ color }}>{value}</div>
    </div>
  );
}
